import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getGrowthRateBreakdown } from '../calculators/growthRateCalculator';
import {
    computeDerivedTelemetry,
    formatValueByUnit,
    listCompoundingParameters,
    listPolicyParameters
} from '../model/parameterRegistry.js';
import { buildCalibrationReport } from '../model/calibrationReport.js';
import { getBacktestComparisons } from '../model/historicalBenchmarks.js';

/**
 * On-Screen Diagnostic Panel
 * Comprehensive inspector for simulation state, params, and calculations.
 */
export default function DiagnosticPanel({ config, results, baselineData, rigorData: rigorDataProp, uncertaintyResult, onRunUncertainty, isRunningUncertainty }) {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosed, setIsClosed] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    // Resizability State
    const [panelSize, setPanelSize] = useState({
        width: Math.min(window.innerWidth - 40, 800),
        height: Math.min(window.innerHeight * 0.8, 800)
    });
    const isResizing = useRef(false);
    const lastMousePos = useRef({ x: 0, y: 0 });

    // Default to a year ~10 years into simulation if possible
    const defaultYear = config.startYear ? config.startYear + 10 : 2037;
    const [selectedYear, setSelectedYear] = useState(defaultYear);

    // Resizing Handlers
    const startResizing = useCallback((e) => {
        e.preventDefault();
        isResizing.current = true;
        lastMousePos.current = { x: e.clientX, y: e.clientY };
        document.body.style.cursor = 'nw-resize';
        document.body.style.userSelect = 'none';

        const onMouseMove = (moveEvent) => {
            if (!isResizing.current) return;
            const deltaX = lastMousePos.current.x - moveEvent.clientX; // Direction: Left increases width
            const deltaY = lastMousePos.current.y - moveEvent.clientY; // Direction: Up increases height

            setPanelSize(prev => ({
                width: Math.max(380, Math.min(window.innerWidth - 20, prev.width + deltaX)),
                height: Math.max(300, Math.min(window.innerHeight - 100, prev.height + deltaY))
            }));

            lastMousePos.current = { x: moveEvent.clientX, y: moveEvent.clientY };
        };

        const onMouseUp = () => {
            isResizing.current = false;
            document.body.style.cursor = 'default';
            document.body.style.userSelect = 'auto';
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    }, []);

    if (isClosed || !results) return null;

    // Safety check: if results.trustFund is missing, we can't show advanced tabs
    const hasTrustFundData = !!results.trustFund;

    // Support Dual Baseline structure (use Scenario/Active results)
    const activeResults = results.scenarioResults || results.yearlyResults;
    if (!results || !activeResults) return null;

    // Slice logic for Diagnostic Panel (Match Dashboard)
    const displayCount = config.simulationYears || 10;
    const yearlyResultsSliced = activeResults.slice(0, displayCount);

    const modelData = {
        years: yearlyResultsSliced.map(y => y.year),
        trustFund: hasTrustFundData ? results.trustFund : { yearlyData: [], summary: {} },
        summary: results.summary
    };

    const yearData = modelData.trustFund.yearlyData.find(y => y.year === selectedYear);
    const growthBreakdown = hasTrustFundData ? getGrowthRateBreakdown(selectedYear) : null;
    const validation = runValidationChecks(config, results, baselineData);

    const rigorData = rigorDataProp || {
        backtest: getBacktestComparisons(),
        calibrationReport: buildCalibrationReport()
    };

    const tabs = [
        { id: 'overview', label: '📊 Overview' },
        { id: 'growth', label: '📈 Growth' },
        { id: 'components', label: '🧩 Effects' },
        { id: 'scenarios', label: '🔄 Scenarios' },
        { id: 'assumptions', label: '📋 Params' },
        { id: 'rigor', label: '🔬 Rigor' },
        { id: 'validation', label: '✅ Valid' }
    ];

    return (
        <div className="diagnostic-panel-container">
            <div className="diagnostic-header" style={{ width: isOpen ? panelSize.width : 400 }}>
                <h3>🔬 System Diagnostics & Telemetry</h3>
                <div className="diag-header-actions">
                    <button className="diag-toggle-btn" onClick={() => setIsOpen(!isOpen)}>
                        {isOpen ? 'Minimize' : 'Expand'}
                    </button>
                    {isOpen && (
                        <button className="diag-close-btn" onClick={() => setIsOpen(false)} title="Collapse Diagnostics">
                            ✖
                        </button>
                    )}
                </div>
            </div>

            {isOpen && (
                <div className="diagnostic-panel" style={{ width: panelSize.width, height: panelSize.height }}>
                    {/* Resize Handle (Top-Left) */}
                    <div className="diag-resize-handle" onMouseDown={startResizing} title="Resize Panel" />

                    {/* Year Selector */}
                    <div className="year-selector">
                        <div className="selector-group">
                            <label>Analysis Year:</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            >
                                {modelData.years.map(year => (
                                    <option key={year} value={year}>
                                        {year} (Year {year - config.startYear})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="tab-navigation">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={activeTab === tab.id ? 'active' : ''}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="tab-content">
                        {activeTab === 'overview' && (
                            yearData ?
                                <OverviewTab yearData={yearData} modelData={modelData} validation={validation} /> :
                                <div className="error-msg">No data for selected year.</div>
                        )}

                        {activeTab === 'growth' && (
                            yearData && growthBreakdown ?
                                <GrowthRatesTab
                                    yearData={yearData}
                                    growthBreakdown={growthBreakdown}
                                    selectedYear={selectedYear}
                                /> : <div className="error-msg">Growth data unavailable.</div>
                        )}

                        {activeTab === 'components' && (
                            yearData && growthBreakdown ?
                                <CompoundingEffectsTab
                                    yearData={yearData}
                                    growthBreakdown={growthBreakdown}
                                /> : <div className="error-msg">Compounding data unavailable.</div>
                        )}

                        {activeTab === 'scenarios' && (
                            yearData ?
                                <ScenariosTab
                                    yearData={yearData}
                                    modelData={modelData}
                                    config={config}
                                /> : <div className="error-msg">Scenario data unavailable.</div>
                        )}

                        {activeTab === 'assumptions' && (
                            <AssumptionsTab policyConfig={config} selectedYear={selectedYear} />
                        )}

                        {activeTab === 'rigor' && (
                            <RigorTab
                                rigorData={rigorData}
                                uncertaintyResult={uncertaintyResult}
                                onRunUncertainty={onRunUncertainty}
                                isRunningUncertainty={isRunningUncertainty}
                            />
                        )}

                        {activeTab === 'validation' && <ValidationTab validation={validation} />}
                    </div>

                    <div className="diagnostic-footer">
                        <button className="diag-close-btn-large" onClick={() => setIsOpen(false)}>
                            Collapse Diagnostics Panel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ... EXISTING VALIDATION LOGIC ...
function runValidationChecks(config, results, baseline) {
    const flags = [];
    const scenarioResults = results?.scenarioResults || results?.yearlyResults || [];
    const trustFundData = results?.trustFund?.yearlyData || [];

    // 1. Trust Fund Solvency (use adjustedEndingBalance = balance after GF transfers)
    if (trustFundData.length > 0) {
        const insolventYears = trustFundData.filter(y => (y.adjustedEndingBalance ?? y.endingBalance ?? 0) < 0);
        if (insolventYears.length > 0) {
            flags.push({
                type: 'warning',
                title: 'Trust Fund Insolvency',
                message: `Trust Fund balance drops below zero in ${insolventYears.length} year(s) (First: ${insolventYears[0].year}).`
            });
        } else {
            flags.push({
                type: 'success',
                title: 'Trust Fund Solvency',
                message: `Trust Fund balance is non-negative in all ${trustFundData.length} projected years.`
            });
        }
    }

    // 2. Reserve ratio within Sec 206 band (5–10%)
    if (trustFundData.length > 0 && config.reserveTargetMin != null && config.reserveTargetMax != null) {
        const outOfBand = trustFundData.filter(y => {
            const r = y.reserveRatio;
            return r != null && (r < config.reserveTargetMin || r > config.reserveTargetMax);
        });
        if (outOfBand.length > 0) {
            flags.push({
                type: 'warning',
                title: 'Reserve Ratio Outside Band',
                message: `Reserve ratio outside ${(config.reserveTargetMin * 100).toFixed(0)}–${(config.reserveTargetMax * 100).toFixed(0)}% in ${outOfBand.length} year(s) (e.g. ${outOfBand[0].year}: ${(outOfBand[0].reserveRatio * 100).toFixed(1)}%).`
            });
        } else {
            flags.push({
                type: 'success',
                title: 'Reserve Ratio',
                message: `Reserve ratio within target band (${(config.reserveTargetMin * 100).toFixed(0)}–${(config.reserveTargetMax * 100).toFixed(0)}%) for all years.`
            });
        }
    }

    // 3. Config within bill / reasonable bounds
    const rateChecks = [];
    if (config.employerRate < 0 || config.employerRate > 0.15) rateChecks.push(`employer rate ${(config.employerRate * 100).toFixed(1)}%`);
    if (config.baseIndividualRate < 0 || config.baseIndividualRate > 0.12) rateChecks.push(`individual rate ${(config.baseIndividualRate * 100).toFixed(1)}%`);
    if (rateChecks.length > 0) {
        flags.push({
            type: 'warning',
            title: 'Rates Outside Typical Bounds',
            message: `Values may be outside bill or scoring range: ${rateChecks.join('; ')}.`
        });
    } else {
        flags.push({
            type: 'success',
            title: 'Financing Rates',
            message: 'Employer and individual rates within typical bill bounds (0–15%, 0–12%).'
        });
    }

    // 4. Data completeness (active years have revenue and spending)
    const startYear = config.startYear ?? 2027;
    const active = scenarioResults.filter(r => r.year >= startYear);
    const missingRevenue = active.filter(r => r.totalRevenue == null || r.totalRevenue === 0);
    const missingSpending = active.filter(r => r.totalSpending == null);
    if (missingRevenue.length > 0 || missingSpending.length > 0) {
        flags.push({
            type: 'warning',
            title: 'Incomplete Scenario Data',
            message: `${missingRevenue.length} year(s) missing revenue; ${missingSpending.length} year(s) missing spending.`
        });
    } else if (active.length > 0) {
        flags.push({
            type: 'success',
            title: 'Scenario Data',
            message: `Revenue and spending present for all ${active.length} active years (${startYear}+).`
        });
    }

    // 5. Summary line when no warnings
    const warnings = flags.filter(f => f.type === 'warning');
    if (warnings.length === 0 && flags.length > 0) {
        flags.push({
            type: 'success',
            title: 'Overall',
            message: 'All validation checks passed. No warnings.'
        });
    }

    return flags;
}

// ===========================================================================
// OVERVIEW TAB
// ===========================================================================

function OverviewTab({ yearData, modelData, validation }) {
    const formatCurrency = (amount) => `$${(amount / 1e9).toFixed(1)}B`;
    const formatPercent = (value) => `${(value * 100).toFixed(2)}%`;

    if (!yearData) return <div>No Data</div>;

    return (
        <div className="overview-tab">
            <h3>Year {yearData.year} Summary</h3>

            {validation.length > 0 && (
                <div className="validation-alert-banner">
                    ⚠️ {validation.length} Validation Warnings Active
                </div>
            )}

            <div className="metrics-grid">
                {/* Financial Health */}
                <div className="metric-card">
                    <h4>💰 Financial Health</h4>
                    <div className="metric-row">
                        <span>Trust Fund Balance:</span>
                        <span className={yearData.adjustedEndingBalance > 0 ? 'positive' : 'negative'}>
                            {formatCurrency(yearData.adjustedEndingBalance)}
                        </span>
                    </div>
                    <div className="metric-row">
                        <span>Annual Net Change:</span>
                        <span className={yearData.adjustedNetChange > 0 ? 'positive' : 'negative'}>
                            {formatCurrency(yearData.adjustedNetChange)}
                        </span>
                    </div>
                    <div className="metric-row">
                        <span>Months of Reserves:</span>
                        <span>{yearData.monthsOfReserves?.toFixed(1) || 'N/A'} months</span>
                    </div>
                    <div className="metric-row">
                        <span>Status:</span>
                        <span style={{ color: yearData.solvencyStatus?.color }}>
                            {yearData.solvencyStatus?.icon} {yearData.solvencyStatus?.status}
                        </span>
                    </div>
                </div>

                {/* Spending Breakdown */}
                <div className="metric-card">
                    <h4>💵 Flows ({formatCurrency(yearData.outflows)})</h4>
                    <div className="metric-row">
                        <span>Inflows (Revenue):</span>
                        <span>{formatCurrency(yearData.inflows)}</span>
                    </div>
                    <div className="metric-row">
                        <span>Outflows (Spend):</span>
                        <span>{formatCurrency(yearData.outflows)}</span>
                    </div>
                    <div className="metric-row">
                        <span>Gen Fund Transfer:</span>
                        <span>{formatCurrency(yearData.actualGeneralFundTransfer)}</span>
                    </div>
                </div>

                {/* Compounding Impact */}
                <div className="metric-card highlight">
                    <h4>🔄 Compounding Effects Impact</h4>
                    <div className="metric-row">
                        <span>Savings This Year:</span>
                        <span className="positive">{formatCurrency(yearData.savingsFromCompounding)}</span>
                    </div>
                    <div className="metric-row">
                        <span>As % of Baseline:</span>
                        <span>{formatPercent(yearData.savingsFromCompoundingPercent / 100)}</span>
                    </div>
                    <div className="metric-row">
                        <span>Growth Rate Reduction:</span>
                        <span>{formatPercent(yearData.growthRates?.reduction || 0)}</span>
                    </div>
                    <div className="metric-row">
                        <span>Sustainability Score:</span>
                        <span className={getSustainabilityColor(yearData.sustainabilityScore)}>
                            {yearData.sustainabilityScore?.toFixed(0)}/100
                        </span>
                    </div>
                </div>
            </div>

            {/* What-If Comparison */}
            <div className="what-if-comparison">
                <h4>🔍 What If There Were NO Compounding Effects?</h4>
                <table>
                    <thead>
                        <tr>
                            <th>Scenario</th>
                            <th>Annual Spending</th>
                            <th>Cumulative Balance</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="current-scenario">
                            <td><strong>With Compounding Effects</strong></td>
                            <td>{formatCurrency(yearData.outflows)}</td>
                            <td className={yearData.adjustedEndingBalance > 0 ? 'positive' : 'negative'}>
                                {formatCurrency(yearData.adjustedEndingBalance)}
                            </td>
                            <td style={{ color: yearData.solvencyStatus?.color }}>
                                {yearData.solvencyStatus?.status}
                            </td>
                        </tr>
                        <tr className="hypothetical-scenario">
                            <td>Without Compounding Effects</td>
                            <td>{formatCurrency(yearData.hypotheticalWithoutCompounding?.spending || 0)}</td>
                            <td className={yearData.hypotheticalWithoutCompounding?.cumulativeBalance > 0 ? 'positive' : 'negative'}>
                                {formatCurrency(yearData.hypotheticalWithoutCompounding?.cumulativeBalance || 0)}
                            </td>
                            <td>
                                {yearData.hypotheticalWithoutCompounding?.wouldBeInsolvent ?
                                    <span style={{ color: 'red' }}>⚠️ INSOLVENT</span> :
                                    <span style={{ color: 'var(--simulator-positive)' }}>✓ Solvent</span>
                                }
                            </td>
                        </tr>
                        <tr className="difference-row">
                            <td><strong>Difference</strong></td>
                            <td className="positive">
                                {formatCurrency((yearData.hypotheticalWithoutCompounding?.spending || 0) - yearData.outflows)} saved
                            </td>
                            <td className="positive">
                                {formatCurrency(yearData.adjustedEndingBalance - (yearData.hypotheticalWithoutCompounding?.cumulativeBalance || 0))} better
                            </td>
                            <td>
                                {yearData.hypotheticalWithoutCompounding?.wouldBeInsolvent && yearData.adjustedEndingBalance > 0 &&
                                    <strong style={{ color: 'var(--simulator-positive)' }}>Compounding effects PREVENT insolvency! ✓</strong>
                                }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ===========================================================================
// GROWTH RATES TAB
// ===========================================================================

function GrowthRatesTab({ yearData, growthBreakdown, selectedYear }) {
    const formatPercent = (value) => `${(value * 100).toFixed(2)}%`;
    const formatCurrency = (amount) => `$${(amount / 1e9).toFixed(1)}B`;

    return (
        <div className="growth-rates-tab">
            <h3>📈 Growth Rate Analysis - Year {selectedYear}</h3>

            <div className="growth-comparison">
                <h4>Baseline vs SAFECARE Growth Rates</h4>
                <table>
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th>Baseline (Current System)</th>
                            <th>SAFECARE</th>
                            <th>Reduction</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Overall NHE</strong></td>
                            <td>{formatPercent(growthBreakdown.baseline.overall)}</td>
                            <td>{formatPercent(growthBreakdown.safecare.overall)}</td>
                            <td className="positive">
                                -{formatPercent(growthBreakdown.baseline.overall - growthBreakdown.safecare.overall)}
                            </td>
                        </tr>
                        <tr>
                            <td>Hospital Services</td>
                            <td>{formatPercent(growthBreakdown.baseline.hospital)}</td>
                            <td>{formatPercent(growthBreakdown.safecare.hospital)}</td>
                            <td className="positive">
                                -{formatPercent(growthBreakdown.baseline.hospital - growthBreakdown.safecare.hospital)}
                            </td>
                        </tr>
                        <tr>
                            <td>Physician Services</td>
                            <td>{formatPercent(growthBreakdown.baseline.physician)}</td>
                            <td>{formatPercent(growthBreakdown.safecare.physician)}</td>
                            <td className="positive">
                                -{formatPercent(growthBreakdown.baseline.physician - growthBreakdown.safecare.physician)}
                            </td>
                        </tr>
                        <tr>
                            <td>Prescription Drugs</td>
                            <td>{formatPercent(growthBreakdown.baseline.drug)}</td>
                            <td>{formatPercent(growthBreakdown.safecare.drug)}</td>
                            <td className="positive">
                                -{formatPercent(growthBreakdown.baseline.drug - growthBreakdown.safecare.drug)}
                            </td>
                        </tr>
                        <tr className="highlight-row">
                            <td>Administration</td>
                            <td>{formatPercent(growthBreakdown.baseline.admin)}</td>
                            <td>{formatPercent(growthBreakdown.safecare.admin)}</td>
                            <td className="positive">
                                {growthBreakdown.safecare.admin < 0 ?
                                    `SHRINKING at ${formatPercent(Math.abs(growthBreakdown.safecare.admin))}!` :
                                    `-${formatPercent(growthBreakdown.baseline.admin - growthBreakdown.safecare.admin)}`
                                }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="compounding-projection">
                <h4>10-Year Compounding Projection</h4>
                <p>Starting from ${formatCurrency(1000 * 1e9)} baseline spending:</p>
                <div className="projection-comparison">
                    <div className="projection-card">
                        <h5>Baseline System</h5>
                        <p className="projection-value">
                            {formatCurrency(growthBreakdown.compoundingProjection.baseline10Year * 1e9)}
                        </p>
                        <p className="projection-label">in 10 years</p>
                    </div>
                    <div className="projection-arrow">→</div>
                    <div className="projection-card safecare">
                        <h5>SAFECARE System</h5>
                        <p className="projection-value">
                            {formatCurrency(growthBreakdown.compoundingProjection.safecare10Year * 1e9)}
                        </p>
                        <p className="projection-label">in 10 years</p>
                    </div>
                </div>
                <div className="projection-savings">
                    <strong>Additional Savings from Compounding:</strong>
                    {formatCurrency((growthBreakdown.compoundingProjection.baseline10Year -
                        growthBreakdown.compoundingProjection.safecare10Year) * 1e9)}
                </div>
            </div>
        </div>
    );
}

// ===========================================================================
// COMPOUNDING EFFECTS TAB
// ===========================================================================

function CompoundingEffectsTab({ yearData, growthBreakdown }) {
    const components = growthBreakdown.reductionEffect.components;
    const totalEffect = Object.values(components).reduce((sum, val) => sum + val, 0);

    return (
        <div className="compounding-effects-tab">
            <h3>🧩 Compounding Effects Breakdown - Year {yearData.year}</h3>

            <div className="effects-summary">
                <h4>Total Growth Rate Reduction: {(totalEffect * 100).toFixed(2)} percentage points</h4>
                <p>Conservative Factor Applied: 61% → <strong>{(totalEffect * 0.61 * 100).toFixed(2)}pp</strong> modeled</p>
            </div>

            <div className="effects-breakdown">
                {/* We map specific keys to cards */}
                <EffectCard
                    title="1. Net vs Gross Pay Effect"
                    effect={components.netVsGrossPay}
                    savings={yearData.componentSavings.netVsGrossPay.savings}
                    mechanism={yearData.componentSavings.netVsGrossPay.mechanism}
                    icon="💰"
                    details={{ 'Mechanism': 'Workers tolerate slower nominal wage growth due to eliminated premiums.' }}
                />
                <EffectCard
                    title="2. Medical Education Debt Elimination"
                    effect={components.medicalEducation}
                    savings={yearData.componentSavings.medicalEducation.savings}
                    mechanism={yearData.componentSavings.medicalEducation.mechanism}
                    icon="📚"
                    details={{ 'Status': `${yearData.componentSavings.medicalEducation.workforceDebtFreePercent}% debt-free` }}
                />
                <EffectCard
                    title="3. Administrative Time Liberation"
                    effect={components.adminTime}
                    savings={yearData.componentSavings.adminTime.savings}
                    mechanism={yearData.componentSavings.adminTime.mechanism}
                    icon="⏰"
                    details={{ 'Productivity': `${yearData.componentSavings.adminTime.productivityGain}% gain` }}
                />
                <EffectCard
                    title="7. Administrative Complexity Cascade"
                    effect={components.adminCascade}
                    savings={yearData.componentSavings.adminCascade.savings}
                    mechanism={yearData.componentSavings.adminCascade.mechanism}
                    icon="📉"
                    priority="highest"
                    details={{ 'Mechanism': 'Single billing system eliminates arms race complexity' }}
                />
                {/* Add others similarly if needed */}
            </div>
        </div>
    );
}

function EffectCard({ title, effect, savings, mechanism, icon, details, priority }) {
    const formatCurrency = (amount) => `$${(amount / 1e9).toFixed(1)}B`;
    const formatPercent = (value) => `${(value * 100).toFixed(2)}%`;

    const cardClass = priority === 'highest' ? 'effect-card highest-impact' : 'effect-card';

    return (
        <div className={cardClass}>
            <h5>{icon} {title}</h5>
            <div className="effect-metrics">
                <div className="metric">
                    <span className="label">Growth Reduction:</span>
                    <span className="value">{formatPercent(effect)}</span>
                </div>
                <div className="metric">
                    <span className="label">Annual Savings:</span>
                    <span className="value positive">{formatCurrency(savings)}</span>
                </div>
            </div>
            <div className="effect-mechanism">
                <strong>Mechanism:</strong> {mechanism}
            </div>
            <div className="effect-details">
                {Object.entries(details).map(([key, value]) => (
                    <div key={key} className="detail-row">
                        <span className="detail-key">{key}:</span> <span className="detail-value">{value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ===========================================================================
// SCENARIOS TAB
// ===========================================================================

function ScenariosTab({ yearData, modelData, config }) {
    // Simplified scenario logic for UI
    return (
        <div className="scenarios-tab">
            <h3>🔄 Scenario Sensitivity</h3>
            <p>Comparing projected Trust Fund balance under different growth assumptions.</p>
            <div className="scenario-explanation">
                Current selection assumes -3.0pp growth reduction (Base Case).
            </div>
            {/* Placeholder for full scenario chart */}
            <div className="breakeven-card">
                <p><strong>Breakeven Growth Reduction Needed:</strong> ~0.8pp</p>
                <p>Current modeled reduction: 3.0pp (Safe margin)</p>
            </div>
        </div>
    );
}

// ===========================================================================
// ASSUMPTIONS TAB
// ===========================================================================

// Helper to format values
function formatParamValue(key, value) {
    if (value === null || value === undefined) return '-';
    if (Array.isArray(value)) return `[${value.map(v => typeof v === 'number' && v < 1 ? (v * 100) + '%' : v).join(', ')}]`;
    if (typeof value === 'boolean') return value ? 'Enabled' : 'Disabled';
    if (typeof value === 'object') return JSON.stringify(value);

    // Percentages
    if (typeof value === 'number' && (
        key.toLowerCase().includes('rate') ||
        key.toLowerCase().includes('share') ||
        key.toLowerCase().includes('uplift') ||
        key.toLowerCase().includes('growth') ||
        value < 1.0 // Heuristic for small decimals being rates
    )) {
        if (value === 0) return '0%';
        if (value === 1) return '100%';
        return `${(value * 100).toFixed(2)}%`;
    }

    // Currency/Large Numbers (if key implies money and value is large)
    if (typeof value === 'number' && value > 1000 && (
        key.toLowerCase().includes('cap') ||
        key.toLowerCase().includes('limit') ||
        key.toLowerCase().includes('threshold')
    )) {
        return `$${value.toLocaleString()}`;
    }

    return String(value);
}

function AssumptionsTab({ policyConfig, selectedYear }) {
    const [onlyChanged, setOnlyChanged] = useState(true);
    const [hideUnregistered, setHideUnregistered] = useState(false);

    const valuesDiffer = (a, b) => {
        if (a === b) return false;
        // Arrays/objects: JSON compare is sufficient for config display.
        if (typeof a === 'object' || typeof b === 'object') {
            try {
                return JSON.stringify(a) !== JSON.stringify(b);
            } catch {
                return true;
            }
        }
        return a !== b;
    };

    const policyEntries = listPolicyParameters(policyConfig, { onlyChanged })
        .filter(e => (hideUnregistered ? e.source?.kind !== 'unregistered' : true));

    const derivedEntries = computeDerivedTelemetry({ config: policyConfig, year: selectedYear });
    const compoundingEntries = listCompoundingParameters();

    const registeredCount = policyEntries.filter(e => e.source?.kind !== 'unregistered').length;
    const totalCount = policyEntries.length;

    const renderEntry = (e) => {
        const sourceKind = e.source?.kind || 'unregistered';
        const citation = e.source?.citation ? ` (${e.source.citation})` : '';
        const excerpt = e.source?.excerpt ? `\n\nBill excerpt:\n${e.source.excerpt}` : '';
        const evidence = Array.isArray(e.source?.evidence) && e.source.evidence.length
            ? `\n\nEvidence notes:\n- ${e.source.evidence.join('\n- ')}`
            : '';
        const displayVal = formatValueByUnit(e.unit, e.value);
        const defaultVal = e.defaultValue === undefined ? null : e.defaultValue;
        const showDefault = defaultVal !== null && defaultVal !== undefined && valuesDiffer(e.value, defaultVal);

        return (
            <div key={e.key} className="param-item">
                <span
                    className="param-key"
                    title={`${e.description || e.key}\n\nSource: ${sourceKind}${citation}${excerpt}${evidence}`}
                >
                    {e.label} <span style={{ opacity: 0.7 }}>[{e.key}]</span>
                </span>
                <span className="param-val" title={`${sourceKind}${citation}`}>{displayVal}</span>
                {showDefault && (
                    <span className="param-val" style={{ opacity: 0.7 }} title="Default value">
                        default: {formatValueByUnit(e.unit, defaultVal)}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="assumptions-tab">
            <h3>📋 Activated Parameters & Telemetry</h3>

            <div className="assumptions-section">
                <h4>Display</h4>
                <div className="param-grid">
                    <div className="param-item">
                        <span className="param-key">Show only changed from default</span>
                        <span className="param-val">
                            <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
                        </span>
                    </div>
                    <div className="param-item">
                        <span className="param-key">Hide unregistered/internal keys</span>
                        <span className="param-val">
                            <input type="checkbox" checked={hideUnregistered} onChange={(e) => setHideUnregistered(e.target.checked)} />
                        </span>
                    </div>
                    <div className="param-item">
                        <span className="param-key">Registry coverage (shown)</span>
                        <span className="param-val">{registeredCount}/{totalCount}</span>
                    </div>
                </div>
            </div>

            <div className="assumptions-section">
                <h4>Derived (Selected Year: {selectedYear})</h4>
                <div className="param-grid">
                    {derivedEntries.map(renderEntry)}
                </div>
            </div>

            <div className="assumptions-section">
                <h4>Policy Config (Activated)</h4>
                <div className="param-grid">
                    {policyEntries.map(renderEntry)}
                </div>
            </div>

            <div className="assumptions-section">
                <h4>Compounding Model Toggles</h4>
                <div className="param-grid">
                    {compoundingEntries.map(renderEntry)}
                </div>
            </div>
        </div>
    );
}

// ===========================================================================
// RIGOR TAB (Backtest, Calibration, Uncertainty)
// ===========================================================================
function RigorTab({ rigorData, uncertaintyResult, onRunUncertainty, isRunningUncertainty }) {
    const backtest = rigorData?.backtest || [];
    const calibration = rigorData?.calibrationReport || {};
    const modules = calibration.modules || [];

    const fmt = (v) => (v == null ? '-' : (typeof v === 'number' && v > 1e9 ? `$${(v / 1e9).toFixed(1)}B` : String(v)));

    return (
        <div className="rigor-tab">
            <h3>🔬 Scientific Rigor & Validation</h3>

            <section className="rigor-section">
                <h4>1. Backtesting vs Historical U.S. Benchmarks</h4>
                <p className="rigor-desc">Model assumptions compared to published historical data (Medicare, IRS tax gap, OECD drug pricing, etc.).</p>
                {backtest.length ? (
                    <table className="rigor-table">
                        <thead>
                            <tr>
                                <th>Module</th>
                                <th>Metric</th>
                                <th>Model</th>
                                <th>Benchmark</th>
                                <th>Error %</th>
                                <th>Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {backtest.map((row, i) => (
                                <tr key={i}>
                                    <td>{row.module}</td>
                                    <td>{row.metric}</td>
                                    <td>{row.unit === 'percent' ? `${Number(row.modelValue).toFixed(1)}%` : fmt(row.modelValue)}</td>
                                    <td>{row.unit === 'percent' ? `${Number(row.benchmarkValue).toFixed(1)}%` : fmt(row.benchmarkValue)}</td>
                                    <td>{row.errorPct != null ? row.errorPct.toFixed(1) + '%' : '—'}</td>
                                    <td title={[row.source, row.note].filter(Boolean).join(' — ')} style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.source}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No backtest comparisons available.</p>
                )}
            </section>

            <section className="rigor-section">
                <h4>2. Per-Module Calibration & Error Report</h4>
                <p className="rigor-desc">Sources, assumed vs empirical values, and error metrics per module. Exported in Telemetry JSON.</p>
                {modules.length ? (
                    <div className="calibration-list">
                        {modules.map((m) => (
                            <div key={m.id} className="calibration-module">
                                <strong>{m.label}</strong>
                                <div className="calibration-sources">Sources: {Array.isArray(m.sources) ? m.sources.join('; ') : m.sources}</div>
                                <div className="calibration-error">Error metric: {m.errorMetric}</div>
                                <div className="calibration-citation">Citation: {m.citation}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p>No calibration modules loaded.</p>
                )}
            </section>

            <section className="rigor-section">
                <h4>3. Uncertainty Bands (Monte Carlo)</h4>
                <p className="rigor-desc">Run multiple scenarios with sampled parameters to get 5th/50th/95th percentile bands on key outputs.</p>
                {onRunUncertainty && (
                    <button
                        type="button"
                        className="diag-toggle-btn"
                        onClick={onRunUncertainty}
                        disabled={isRunningUncertainty}
                    >
                        {isRunningUncertainty ? 'Running…' : 'Run uncertainty analysis (50 runs)'}
                    </button>
                )}
                {uncertaintyResult && (
                    <div className="uncertainty-summary">
                        <div className="metric-row"><span>Runs:</span><span>{uncertaintyResult.runCount}</span></div>
                        <div className="metric-row"><span>Sampled parameters:</span><span>{uncertaintyResult.sampledKeys?.join(', ')}</span></div>
                        <h5>Summary percentiles</h5>
                        <div className="metric-row">
                            <span>Average surplus (5th / 50th / 95th):</span>
                            <span>
                                {fmt(uncertaintyResult.summaryPercentiles?.averageSurplus?.[5])} / {fmt(uncertaintyResult.summaryPercentiles?.averageSurplus?.[50])} / {fmt(uncertaintyResult.summaryPercentiles?.averageSurplus?.[95])}
                            </span>
                        </div>
                        <div className="metric-row">
                            <span>Final trust fund (5th / 50th / 95th):</span>
                            <span>
                                {fmt(uncertaintyResult.summaryPercentiles?.finalTrustFundBalance?.[5])} / {fmt(uncertaintyResult.summaryPercentiles?.finalTrustFundBalance?.[50])} / {fmt(uncertaintyResult.summaryPercentiles?.finalTrustFundBalance?.[95])}
                            </span>
                        </div>
                        <p className="rigor-note">Full percentiles by year and bands are included in Export Telemetry (JSON).</p>
                    </div>
                )}
            </section>
        </div>
    );
}

// ===========================================================================
// VALIDATION TAB 
// ===========================================================================
function ValidationTab({ validation }) {
    const warnings = validation.filter(v => v.type === 'warning');
    if (validation.length === 0) {
        return <div className="validation-item success">✅ No data to validate.</div>;
    }
    return (
        <div className="validation-tab">
            {warnings.length === 0 && (
                <div className="validation-summary success">✅ All checks passed</div>
            )}
            {warnings.length > 0 && (
                <div className="validation-summary warning">⚠️ {warnings.length} warning{warnings.length !== 1 ? 's' : ''}</div>
            )}
            <div className="validation-list">
                {validation.map((v, i) => (
                    <div key={i} className={`validation-item ${v.type}`}>
                        <h4>{v.type === 'success' ? '✅' : '⚠️'} {v.title}</h4>
                        <p>{v.message}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}


function getSustainabilityColor(score) {
    if (score >= 80) return 'green';
    if (score >= 60) return 'lightgreen';
    if (score >= 40) return 'yellow';
    if (score >= 20) return 'orange';
    return 'red';
}
